import { useState, useEffect, useRef, useCallback } from 'react';
import { getTransactionHistory, getTransactionDetail } from '../services/subscriberWalletService';

const PAGE_SIZE = 25;

export const useTransactionHistory = (id, currentRole, txnCategory, txnPage, txnSearch) => {
  const [transactions, setTransactions] = useState([]);
  const [txnPagination, setTxnPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const isTxnLoadingRef = useRef(false);
  const lastTxnApiCallRef = useRef({ id: null, role: null, category: null });

  useEffect(() => {
    if (!id || !currentRole || isTxnLoadingRef.current) return;
    
    // Prevent duplicate API calls
    const callKey = `${id}-${currentRole}-${txnCategory}-${txnPage}-${txnSearch}`;
    if (lastTxnApiCallRef.current.callKey === callKey) return;
    
    isTxnLoadingRef.current = true;
    lastTxnApiCallRef.current.callKey = callKey;
    
    const loadTransactionHistory = async () => {
      try {
        setIsLoading(true);
        const params = {
          page: txnPage,
          items: PAGE_SIZE
        };
        
        if (txnSearch && txnSearch.trim()) {
          params.search = txnSearch.trim();
        }
        
        if (txnCategory && txnCategory !== 'All') {
          const categoryMap = {
            'Diagnostics': 'diagnostics',
            'Medicines': 'medicines',
            'Hospital': 'hospital',
            'Loans': 'loans',
            'Hospital Cash Allowance': 'hospital_cash_allowance'
          };
          const apiCategory = categoryMap[txnCategory] || txnCategory.toLowerCase();
          params.service_type = apiCategory;
        }
        
        const response = await getTransactionHistory(id, currentRole, params);
        
        if (response && response.msg && response.msg.items && Array.isArray(response.msg.items)) {
          setTransactions(response.msg.items);
          if (response.msg.pagination) {
            const calculatedTotalPages = Math.ceil(response.msg.pagination.total / response.msg.pagination.items);
            setTxnPagination({
              ...response.msg.pagination,
              total_pages: calculatedTotalPages
            });
          } else {
            setTxnPagination({ page: txnPage, items: PAGE_SIZE, total: response.msg.items.length, total_pages: Math.ceil(response.msg.items.length / PAGE_SIZE) });
          }
        } else if (response && response.items && Array.isArray(response.items)) {
          setTransactions(response.items);
          if (response.pagination) {
            const calculatedTotalPages = Math.ceil(response.pagination.total / response.pagination.items);
            setTxnPagination({
              ...response.pagination,
              total_pages: calculatedTotalPages
            });
          } else {
            setTxnPagination({ page: txnPage, items: PAGE_SIZE, total: response.items.length, total_pages: Math.ceil(response.items.length / PAGE_SIZE) });
          }
        } else if (response && response.data && response.data.items && Array.isArray(response.data.items)) {
          setTransactions(response.data.items);
          if (response.data.pagination) {
            const calculatedTotalPages = Math.ceil(response.data.pagination.total / response.data.pagination.items);
            setTxnPagination({
              ...response.data.pagination,
              total_pages: calculatedTotalPages
            });
          } else {
            setTxnPagination({ page: txnPage, items: PAGE_SIZE, total: response.data.items.length, total_pages: Math.ceil(response.data.items.length / PAGE_SIZE) });
          }
        } else if (Array.isArray(response)) {
          setTransactions(response);
          setTxnPagination({ page: 1, items: PAGE_SIZE, total: response.length, total_pages: Math.ceil(response.length / PAGE_SIZE) });
        } else {
          setTransactions([]);
          setTxnPagination({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });
        }
      } catch (error) {
        console.error('Error loading transaction history:', error);
        setTransactions([]);
        setTxnPagination({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });
      } finally {
        setIsLoading(false);
        isTxnLoadingRef.current = false;
      }
    };
    
    loadTransactionHistory();
  }, [id, currentRole, txnCategory, txnPage, txnSearch]);

  return {
    transactions,
    txnPagination,
    isLoading
  };
};

export const useTransactionDetail = (id, currentRole) => {
  const [transactionDetail, setTransactionDetail] = useState(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const loadTransactionDetail = useCallback(async (transactionId) => {
    if (!transactionId || !id || !currentRole) return;
    
    setSelectedTransactionId(transactionId);
    setDetailLoading(true);
    setDetailError(null);
    setTransactionDetail(null);
    
    try {
      const response = await getTransactionDetail(id, transactionId, currentRole);
      
      if (response && response.metadata) {
        if (response.metadata.api_status === false) {
          const errorMessage = response.msg?.error || 'Failed to load transaction details';
          setDetailError(errorMessage);
          return;
        }
        
        if (response.msg) {
          setTransactionDetail(response.msg);
          return;
        }
      }
      
      if (response && response.data) {
        setTransactionDetail(response.data);
        return;
      }
      
      setDetailError('Invalid response format from server');
    } catch (error) {
      console.error('Error loading transaction detail:', error);
      let errorMessage = 'Failed to load transaction details';
      if (error.message) {
        errorMessage = error.message;
        try {
          const errorMatch = error.message.match(/API Error \d+[^:]*:\s*(.+)/);
          if (errorMatch) {
            const errorJson = JSON.parse(errorMatch[1]);
            if (errorJson.msg?.error) {
              errorMessage = errorJson.msg.error;
            }
          }
        } catch {
          // Use original error message
        }
      }
      setDetailError(errorMessage);
    } finally {
      setDetailLoading(false);
    }
  }, [id, currentRole]);

  const closeModal = useCallback(() => {
    setSelectedTransactionId(null);
    setTransactionDetail(null);
    setDetailError(null);
  }, []);

  return {
    transactionDetail,
    selectedTransactionId,
    detailLoading,
    detailError,
    loadTransactionDetail,
    closeModal
  };
};

